import { useState } from "react";
import { NinjaArt } from "../art/NinjaArt";
import { MODE_INFO, type Mode } from "../content/modes";
import { RANKS } from "../content/ranks";
import { enabledOps } from "../game/modes";
import { placementResult, placementSource } from "../game/placement";
import { mathRandomRng } from "../game/rng";
import type { Answer } from "../game/round";
import type { SaveState } from "../save/schema";
import { Play } from "./Play";
import "./Placement.css";

interface Props {
  save: SaveState;
  /** From Parents, the grown-up can adjust the suggestions before saving. */
  adjustable: boolean;
  onDone: (ranks: Partial<Record<Mode, number>>) => void;
  onCancel: () => void;
}

const STRAND_WORDS: Record<Mode, string> = {
  add: "adding",
  sub: "taking away",
  mul: "times",
  div: "sharing",
  mix: "mix",
};

function listWords(words: string[]): string {
  return words.length <= 1
    ? (words[0] ?? "")
    : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

export function Placement({ save, adjustable, onDone, onCancel }: Props) {
  const ops = enabledOps(save.settings.ops);
  const [phase, setPhase] = useState<"intro" | "playing" | "summary">("intro");
  const [chosen, setChosen] = useState<Partial<Record<Mode, number>>>({});

  const finished = (log: Answer[]) => {
    const suggested = placementResult(ops, log);
    // Placement never lowers a rank already held.
    const out: Partial<Record<Mode, number>> = {};
    for (const [m, r] of Object.entries(suggested) as [Mode, number][])
      out[m] = Math.max(r, save.rank[m]);
    setChosen(out);
    setPhase("summary");
  };

  if (phase === "playing")
    return (
      <Play
        context={{ next: placementSource(ops, mathRandomRng), seconds: null }}
        countdown={false}
        label="Quick check"
        end="stop"
        audio={save.settings.audio}
        onFinished={(r) => finished(r.log)}
        onStopped={onCancel}
      />
    );

  if (phase === "intro")
    return (
      <section className="screen placement">
        <h2 className="display placement-title">Quick check</h2>
        <div className="panel">
          <p>
            A few questions on {listWords(ops.map((o) => STRAND_WORDS[o]))}. There&apos;s no clock.
            Answer what you can. It stops when the questions get tricky.
          </p>
          <button type="button" className="big display" onClick={() => setPhase("playing")}>
            Start the check
          </button>
          <button type="button" className="ghost" onClick={onCancel}>
            Back
          </button>
        </div>
      </section>
    );

  const modes = Object.keys(chosen) as Mode[];
  return (
    <section className="screen placement">
      <h2 className="display placement-title">Your starting ranks</h2>
      <ul className="placement-list">
        {modes.map((m) => {
          const r = chosen[m] ?? 0;
          const rank = RANKS[r]!;
          return (
            <li
              key={m}
              className="panel placement-row"
              style={{ "--c": rank.colour } as React.CSSProperties}
            >
              <NinjaArt rank={rank} className="placement-art" />
              <div className="placement-text">
                <span className="placement-strand">{MODE_INFO[m].label}</span>
                {adjustable ? (
                  <select
                    aria-label={`${MODE_INFO[m].label} starting rank`}
                    value={r}
                    onChange={(e) => setChosen((c) => ({ ...c, [m]: Number(e.target.value) }))}
                  >
                    {RANKS.map((x, i) =>
                      i < save.rank[m] ? null : (
                        <option key={x.name} value={i}>
                          {x.name}
                        </option>
                      ),
                    )}
                  </select>
                ) : (
                  <span className="display placement-rank">{rank.name}</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <p className="hint placement-hint">
        {adjustable
          ? "These are suggestions. Change any of them, then save. Ranks already earned are kept."
          : "From here, pass each rank's trial to climb higher."}
      </p>
      <button type="button" className="big display placement-go" onClick={() => onDone(chosen)}>
        {adjustable ? "Save these ranks" : "Start training"}
      </button>
    </section>
  );
}
